import React, { useEffect, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import {
  getDeliveryPricing,
  updateDeliveryPricing,
  getDeliveryCompanies,
  getDeliveryCompanyPricing,
  updateDeliveryCompanyPricing,
  getDeliveryCompanyExclusions,
  updateDeliveryCompanyExclusions,
  getDistricts,
  getCities,
} from "../../api/adminService";
import { formatCurrency } from "./constants";

const TIER_LABELS = {
  0: "Same City",
  1: "Same District",
  2: "Cross District",
  3: "Cross Border",
};

const COUNTRIES = ["IL", "PS"];

export default function DeliveryPricing() {
  const [companies, setCompanies] = useState([]);
  const [tab, setTab] = useState("default");

  useEffect(() => {
    getDeliveryCompanies()
      .then(setCompanies)
      .catch(() => setCompanies([]));
  }, []);

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 3 }}>
        Delivery Pricing
      </Typography>

      <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ mb: 3 }}>
        <Tab value="default" label="Platform Default" />
        {companies.map((c) => (
          <Tab key={c.userId} value={String(c.userId)} label={c.companyName} />
        ))}
      </Tabs>

      {tab === "default" ? (
        <PlatformDefaultTable />
      ) : (
        <>
          <CompanyPricingGrid companyId={Number(tab)} />
          <ExclusionsSection companyId={Number(tab)} />
        </>
      )}
    </Box>
  );
}

function PlatformDefaultTable() {
  const [rows, setRows] = useState([]);
  const [edits, setEdits] = useState({});
  const [error, setError] = useState("");
  const [savingId, setSavingId] = useState(null);

  useEffect(() => {
    getDeliveryPricing()
      .then(setRows)
      .catch(() => setError("Could not load delivery pricing. Is the API running?"));
  }, []);

  function handleChange(id, value) {
    setEdits((prev) => ({ ...prev, [id]: value }));
  }

  async function handleSave(id) {
    const value = edits[id];
    if (value === undefined) return;
    setSavingId(id);
    try {
      const updated = await updateDeliveryPricing(id, Number(value));
      setRows((prev) => prev.map((r) => (r.deliveryPricingRuleId === id ? updated : r)));
      setEdits((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
    } catch {
      setError("Could not save this price.");
    } finally {
      setSavingId(null);
    }
  }

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Used as the fallback price for any delivery company that hasn't set its own price for a route.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ bgcolor: "background.paper", borderRadius: 2, overflow: "hidden" }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Country</TableCell>
              <TableCell>Tier</TableCell>
              <TableCell align="right">Current Price</TableCell>
              <TableCell align="right" width={200}>
                New Price
              </TableCell>
              <TableCell align="center" width={80}>
                Save
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.deliveryPricingRuleId} hover>
                <TableCell>{row.country ?? "Any"}</TableCell>
                <TableCell>{TIER_LABELS[row.tier] ?? row.tier}</TableCell>
                <TableCell align="right">{formatCurrency(row.price)}</TableCell>
                <TableCell align="right">
                  <TextField
                    size="small"
                    type="number"
                    value={edits[row.deliveryPricingRuleId] ?? ""}
                    placeholder={String(row.price)}
                    onChange={(e) => handleChange(row.deliveryPricingRuleId, e.target.value)}
                    sx={{ width: 140 }}
                  />
                </TableCell>
                <TableCell align="center">
                  <IconButton
                    color="primary"
                    disabled={
                      edits[row.deliveryPricingRuleId] === undefined || savingId === row.deliveryPricingRuleId
                    }
                    onClick={() => handleSave(row.deliveryPricingRuleId)}
                  >
                    <SaveIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    </Box>
  );
}

function CompanyPricingGrid({ companyId }) {
  const [data, setData] = useState(null);
  const [edits, setEdits] = useState({});
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    setData(null);
    setEdits({});
    setError("");
    setSaved(false);
    getDeliveryCompanyPricing(companyId)
      .then(setData)
      .catch(() => setError("Could not load this company's pricing. Is the API running?"));
  }, [companyId]);

  if (error) return <Alert severity="error">{error}</Alert>;
  if (!data) return null;

  function fieldKey(country, field) {
    return country ? `${country}_${field}` : field;
  }

  function valueFor(country, field, dbValue) {
    const key = fieldKey(country, field);
    return edits[key] !== undefined ? edits[key] : dbValue ?? "";
  }

  function handleChange(country, field, value) {
    setEdits((prev) => ({ ...prev, [fieldKey(country, field)]: value }));
    setSaved(false);
  }

  async function handleSave() {
    setSaving(true);
    setError("");
    try {
      const payload = {
        countries: COUNTRIES.map((country) => {
          const row = data.countries.find((c) => c.country === country) ?? {};
          const parse = (field, dbValue) => {
            const raw = valueFor(country, field, dbValue);
            return raw === "" ? null : Number(raw);
          };
          return {
            country,
            sameCityPrice: parse("sameCityPrice", row.sameCityPrice),
            sameDistrictPrice: parse("sameDistrictPrice", row.sameDistrictPrice),
            crossDistrictPrice: parse("crossDistrictPrice", row.crossDistrictPrice),
          };
        }),
        crossBorderPrice: (() => {
          const raw = valueFor(null, "crossBorderPrice", data.crossBorderPrice);
          return raw === "" ? null : Number(raw);
        })(),
        specialDeliveryFee: (() => {
          const raw = valueFor(null, "specialDeliveryFee", data.specialDeliveryFee);
          return raw === "" ? null : Number(raw);
        })(),
      };
      const updated = await updateDeliveryCompanyPricing(companyId, payload);
      setData(updated);
      setEdits({});
      setSaved(true);
    } catch {
      setError("Could not save this company's pricing.");
    } finally {
      setSaving(false);
    }
  }

  const tierFields = [
    { field: "sameCityPrice", defaultField: "defaultSameCityPrice", label: TIER_LABELS[0] },
    { field: "sameDistrictPrice", defaultField: "defaultSameDistrictPrice", label: TIER_LABELS[1] },
    { field: "crossDistrictPrice", defaultField: "defaultCrossDistrictPrice", label: TIER_LABELS[2] },
  ];

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Leave a field blank to use the platform default price (shown as the placeholder) for that route.
      </Typography>

      {saved && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Saved.
        </Alert>
      )}

      <Box sx={{ bgcolor: "background.paper", borderRadius: 2, overflow: "hidden" }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Tier</TableCell>
              {COUNTRIES.map((country) => (
                <TableCell key={country} align="right">
                  {country}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {tierFields.map(({ field, defaultField, label }) => (
              <TableRow key={field} hover>
                <TableCell>{label}</TableCell>
                {COUNTRIES.map((country) => {
                  const row = data.countries.find((c) => c.country === country) ?? {};
                  return (
                    <TableCell key={country} align="right">
                      <TextField
                        size="small"
                        type="number"
                        value={valueFor(country, field, row[field])}
                        placeholder={row[defaultField] != null ? String(row[defaultField]) : "-"}
                        onChange={(e) => handleChange(country, field, e.target.value)}
                        sx={{ width: 120 }}
                      />
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
            <TableRow hover>
              <TableCell>{TIER_LABELS[3]}</TableCell>
              <TableCell align="right" colSpan={COUNTRIES.length}>
                <TextField
                  size="small"
                  type="number"
                  value={valueFor(null, "crossBorderPrice", data.crossBorderPrice)}
                  placeholder={data.defaultCrossBorderPrice != null ? String(data.defaultCrossBorderPrice) : "-"}
                  onChange={(e) => handleChange(null, "crossBorderPrice", e.target.value)}
                  sx={{ width: 120 }}
                />
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </Box>

      <Typography variant="subtitle2" sx={{ mt: 3, mb: 1 }}>
        Special Delivery Fee
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Flat surcharge added on top of the route price whenever an order includes a product marked as
        needing special delivery (bulky or fragile items). Leave blank for no surcharge.
      </Typography>
      <TextField
        size="small"
        type="number"
        value={valueFor(null, "specialDeliveryFee", data.specialDeliveryFee)}
        placeholder="0"
        onChange={(e) => handleChange(null, "specialDeliveryFee", e.target.value)}
        sx={{ width: 160 }}
      />

      <Box>
        <Button variant="contained" sx={{ mt: 2 }} disabled={saving} onClick={handleSave}>
          Save
        </Button>
      </Box>
    </Box>
  );
}

function ExclusionsSection({ companyId }) {
  const [data, setData] = useState(null);
  const [districtOptions, setDistrictOptions] = useState([]);
  const [districtsLoading, setDistrictsLoading] = useState(true);
  const [cityInput, setCityInput] = useState("");
  const [cityOptions, setCityOptions] = useState([]);
  const [cityOptionsLoading, setCityOptionsLoading] = useState(false);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    setData(null);
    setError("");
    setSaved(false);
    getDeliveryCompanyExclusions(companyId)
      .then(setData)
      .catch(() => setError("Could not load this company's exclusions. Is the API running?"));
  }, [companyId]);

  useEffect(() => {
    getDistricts()
      .then(setDistrictOptions)
      .catch(() => setDistrictOptions([]))
      .finally(() => setDistrictsLoading(false));
  }, []);

  useEffect(() => {
    setCityOptionsLoading(true);
    const timeout = setTimeout(() => {
      getCities({ search: cityInput, pageSize: 20 })
        .then((result) =>
          setCityOptions(result.items.map((c) => ({ cityId: c.cityId, cityName: c.nameHebrew })))
        )
        .catch(() => setCityOptions([]))
        .finally(() => setCityOptionsLoading(false));
    }, 300);
    return () => clearTimeout(timeout);
  }, [cityInput]);

  if (error) return <Alert severity="error">{error}</Alert>;
  if (!data) return null;

  function toggleCountry(country) {
    setData((prev) => ({
      ...prev,
      excludedCountries: prev.excludedCountries.includes(country)
        ? prev.excludedCountries.filter((c) => c !== country)
        : [...prev.excludedCountries, country],
    }));
    setSaved(false);
  }

  async function handleSave() {
    setSaving(true);
    setError("");
    try {
      const updated = await updateDeliveryCompanyExclusions(companyId, {
        excludedCountries: data.excludedCountries,
        excludedDistricts: data.excludedDistricts,
        excludedCityIds: data.excludedCities.map((c) => c.cityId),
      });
      setData(updated);
      setSaved(true);
    } catch {
      setError("Could not save the exclusions.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Box sx={{ mt: 4 }}>
      <Typography variant="h6" sx={{ mb: 1 }}>
        Excluded Areas
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        This company will never be offered to customers delivering to these countries, districts, or cities,
        even if it's otherwise eligible. For example, exclude "PS" to keep a company IL-only.
      </Typography>

      {saved && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Saved.
        </Alert>
      )}

      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        Countries
      </Typography>
      {COUNTRIES.map((country) => (
        <FormControlLabel
          key={country}
          control={
            <Checkbox checked={data.excludedCountries.includes(country)} onChange={() => toggleCountry(country)} />
          }
          label={country}
        />
      ))}

      <Typography variant="subtitle2" sx={{ mt: 3, mb: 1 }}>
        Districts
      </Typography>
      <Autocomplete
        multiple
        options={districtOptions}
        loading={districtsLoading}
        value={data.excludedDistricts}
        onChange={(e, value) => {
          setData((prev) => ({ ...prev, excludedDistricts: value }));
          setSaved(false);
        }}
        renderTags={(value, getTagProps) =>
          value.map((option, index) => <Chip size="small" label={option} {...getTagProps({ index })} key={option} />)
        }
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={data.excludedDistricts.length ? "" : "Select districts"}
            size="small"
          />
        )}
        sx={{ maxWidth: 500 }}
      />

      <Typography variant="subtitle2" sx={{ mt: 3, mb: 1 }}>
        Cities
      </Typography>
      <Autocomplete
        multiple
        options={cityOptions}
        loading={cityOptionsLoading}
        filterOptions={(x) => x}
        value={data.excludedCities}
        onChange={(e, value) => {
          setData((prev) => ({ ...prev, excludedCities: value }));
          setSaved(false);
        }}
        onInputChange={(e, value) => setCityInput(value)}
        getOptionLabel={(option) => option.cityName}
        isOptionEqualToValue={(option, value) => option.cityId === value.cityId}
        renderTags={(value, getTagProps) =>
          value.map((option, index) => (
            <Chip size="small" label={option.cityName} {...getTagProps({ index })} key={option.cityId} />
          ))
        }
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={data.excludedCities.length ? "" : "Search for a city to exclude"}
            size="small"
          />
        )}
        sx={{ maxWidth: 500 }}
      />

      <Button variant="contained" sx={{ mt: 3 }} disabled={saving} onClick={handleSave}>
        Save
      </Button>
    </Box>
  );
}
